import { create } from "zustand";
import {
  EmployeeFullInfo,
  EmployeeInfo,
  EmployeeInfoUpdate,
} from "@/modules/types/employee";
import { employeeServiceInstance } from "@/modules/services/employee/employeeService";
import { AppError } from "@/lib/utils/errors";

interface EmployeeStoreState {
  employeeInfo: EmployeeInfo | null;
  isLoadingEmployee: boolean;
  isUpdateLoading: boolean;
  error?: string;
  updateSuccess?: boolean;
  getInfoError?: string;
}

interface EmployeeStoreActions {
  getEmployeeInfo: () => Promise<void>;
  updateEmployeeInfo: (
    employeeId: string,
    data: EmployeeInfoUpdate
  ) => Promise<void>;
  uploadAvatar: (
    employeeId: string,
    file: File,
    fileName: string
  ) => Promise<void>;
  setEmployeeAvatar: (avatar: string) => void;
  setError: (error: string | undefined) => void;
  setUpdateSuccess: (success: boolean) => void;
  setIsLoadingEmployee: (isLoading: boolean) => void;
  setUpdateLoading: (isLoading: boolean) => void;
  reset: () => void;
}

export type EmployeeStore = EmployeeStoreState & EmployeeStoreActions;

const initialState: EmployeeStoreState = {
  employeeInfo: null,
  isLoadingEmployee: false,
  isUpdateLoading: false,
};

export const useEmployeeStore = create<EmployeeStore>((set) => ({
  ...initialState,

  getEmployeeInfo: async () => {
    set({ isLoadingEmployee: true, getInfoError: undefined });
    try {
      const resp = await employeeServiceInstance.getByDefault();
      if (resp && resp.employeeInfo) {
        set({ employeeInfo: resp.employeeInfo, isLoadingEmployee: false });
      } else {
        set({ getInfoError: "Employee not found", isLoadingEmployee: false });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      set({ getInfoError: appErr.message, isLoadingEmployee: false });
    }
  },

  updateEmployeeInfo: async (employeeId: string, data: EmployeeInfoUpdate) => {
    set({ isUpdateLoading: true, error: undefined, updateSuccess: false });
    try {
      const resp = await employeeServiceInstance.update(employeeId, data);
      if (resp) {
        set((state) => ({
          employeeInfo: state.employeeInfo
            ? { ...state.employeeInfo, ...data }
            : null,
          isUpdateLoading: false,
          updateSuccess: true,
        }));
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      set({ error: appErr.message, isUpdateLoading: false });
    }
  },

  uploadAvatar: async (employeeId: string, file: File, fileName: string) => {
    set({ isUpdateLoading: true, error: undefined });
    try {
      const avatarUrl = await employeeServiceInstance.uploadAvatar(
        employeeId,
        file,
        fileName
      );
      if (avatarUrl) {
        await employeeServiceInstance.updateAvatar(employeeId, avatarUrl);
        set((state) => ({
          employeeInfo: state.employeeInfo
            ? { ...state.employeeInfo, avatar: avatarUrl }
            : null,
          isUpdateLoading: false,
        }));
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      set({ error: appErr.message, isUpdateLoading: false });
    }
  },

  setEmployeeAvatar: (avatar: string) =>
    set((state) => ({
      employeeInfo: state.employeeInfo
        ? { ...state.employeeInfo, avatar }
        : null,
    })),

  setError: (error) => set({ error }),
  setUpdateSuccess: (updateSuccess) => set({ updateSuccess }),
  setIsLoadingEmployee: (isLoadingEmployee) => set({ isLoadingEmployee }),
  setUpdateLoading: (isUpdateLoading) => set({ isUpdateLoading }),
  reset: () => set(initialState),
}));
